import Decimal from 'decimal.js';

/**
 * Mọi phép tính tiền dùng Decimal (độ chính xác 40 chữ số) — không dùng số thực JS.
 * Quy tắc làm tròn VND: làm tròn tới đồng (0 chữ số thập phân), ROUND_HALF_UP,
 * thực hiện tại từng dòng thành tiền / mỗi kỳ trả nợ / chiết khấu / thuế.
 */
export const D = Decimal.clone({ precision: 40, rounding: Decimal.ROUND_HALF_UP });
export type Dec = InstanceType<typeof D>;
export type DecimalInput = string | number | Dec;

export function dec(v: DecimalInput | null | undefined): Dec {
  if (v === null || v === undefined || v === '') return new D(0);
  return new D(v as Decimal.Value);
}

/** Làm tròn tới đồng VND. */
export function roundVnd(v: DecimalInput): Dec {
  return dec(v).toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
}

/** Chuỗi số nguyên VND để lưu / truyền qua API (không có dấu phân cách). */
export function vndString(v: DecimalInput): string {
  return roundVnd(v).toFixed(0);
}

export function decString(v: DecimalInput, dp = 4): string {
  return dec(v).toDecimalPlaces(dp, Decimal.ROUND_HALF_UP).toFixed();
}

function groupThousands(intPart: string): string {
  return intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** 125000000 -> "125.000.000 ₫" (định dạng Việt Nam). */
export function formatVnd(v: DecimalInput | null | undefined, withSymbol = true): string {
  if (v === null || v === undefined || v === '') return '';
  const r = roundVnd(v);
  const neg = r.isNegative() && !r.isZero();
  const s = groupThousands(r.abs().toFixed(0));
  return `${neg ? '-' : ''}${s}${withSymbol ? ' ₫' : ''}`;
}

/** Số thập phân kiểu Việt Nam: 5.5 -> "5,5"; 1234.5 -> "1.234,5". */
export function formatNumberVi(v: DecimalInput | null | undefined, maxDp = 2): string {
  if (v === null || v === undefined || v === '') return '';
  const r = dec(v).toDecimalPlaces(maxDp, Decimal.ROUND_HALF_UP);
  const neg = r.isNegative() && !r.isZero();
  const [i, f] = r.abs().toFixed().split('.');
  return `${neg ? '-' : ''}${groupThousands(i)}${f ? ',' + f : ''}`;
}

export function formatPercentVi(v: DecimalInput | null | undefined, maxDp = 2): string {
  if (v === null || v === undefined || v === '') return '';
  return `${formatNumberVi(v, maxDp)}%`;
}

const DIGITS = ['không', 'một', 'hai', 'ba', 'bốn', 'năm', 'sáu', 'bảy', 'tám', 'chín'];
const UNITS = ['', ' nghìn', ' triệu', ' tỷ', ' nghìn tỷ', ' triệu tỷ'];

function readTriple(n: number, full: boolean): string {
  const h = Math.floor(n / 100);
  const t = Math.floor((n % 100) / 10);
  const u = n % 10;
  const parts: string[] = [];
  if (full || h > 0) parts.push(`${DIGITS[h]} trăm`);
  if (t > 1) {
    parts.push(`${DIGITS[t]} mươi`);
    if (u === 1) parts.push('mốt');
    else if (u === 4) parts.push('tư');
    else if (u === 5) parts.push('lăm');
    else if (u > 0) parts.push(DIGITS[u]);
  } else if (t === 1) {
    parts.push('mười');
    if (u === 5) parts.push('lăm');
    else if (u > 0) parts.push(DIGITS[u]);
  } else if (u > 0) {
    if (full || h > 0) parts.push('linh');
    parts.push(DIGITS[u]);
  }
  return parts.join(' ');
}

/** Đọc số tiền bằng chữ tiếng Việt: 1250000 -> "Một triệu hai trăm năm mươi nghìn đồng". */
export function vndInWords(v: DecimalInput): string {
  let r = roundVnd(v);
  if (r.isZero()) return 'Không đồng';
  const neg = r.isNegative();
  r = r.abs();
  const groups: number[] = [];
  let s = r.toFixed(0);
  while (s.length > 0) {
    groups.unshift(Number(s.slice(-3)));
    s = s.slice(0, -3);
  }
  const words: string[] = [];
  groups.forEach((g, idx) => {
    const unitIdx = groups.length - 1 - idx;
    if (g === 0) return;
    words.push(readTriple(g, idx > 0) + UNITS[unitIdx]);
  });
  const text = words.join(' ').replace(/\s+/g, ' ').trim();
  const out = `${neg ? 'âm ' : ''}${text} đồng`;
  return out.charAt(0).toUpperCase() + out.slice(1);
}
